import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomModal from "../../common/CustomModal";
import CustomInput from "../../common/CustomInput";
import AgencyAPIs from "../../../apiRoutes/Agency";
import { GET_AGENCIES_QUERY_KEY } from "../../../utils/constants/queryKeys";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import toast from "react-hot-toast";
import { Switch } from "../../ui/switch";

const AddAgencyModal = ({ open, onClose, editData = null }) => {
  const [formData, setFormData] = useState({
    name: editData?.name || "",
    domain: editData?.domain || "",
    notes: editData?.notes || "",
    allow_direct_delivery: !!editData?.allow_direct_delivery,
  });
  const [status, setStatus] = useState(editData?.status ?? "");
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (editData?.status !== undefined) setStatus(editData.status);
  }, [editData?.status]);

  const { mutate: submitAgency, isPending } = useMutation({
    mutationFn: (payload) =>
      editData
        ? AgencyAPIs.update({ axiosPrivate, id: editData.id, payload })
        : AgencyAPIs.create({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AGENCIES_QUERY_KEY });
      toast.success(`Agency ${editData ? "updated" : "created"} successfully.`);
      onClose();
    },
  });

  const { mutate: updateStatus, isPending: isStatusUpdating } = useMutation({
    mutationFn: (newStatus) =>
      AgencyAPIs.update({
        axiosPrivate,
        id: editData.id,
        payload: { status: newStatus },
      }),
    onSuccess: (_, newStatus) => {
      setStatus(newStatus);
      queryClient.invalidateQueries({ queryKey: GET_AGENCIES_QUERY_KEY });
      toast.success(
        `Agency ${newStatus === "approved" ? "approved" : "rejected"}.`
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error_message || error?.message);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = formData.name?.trim() ?? "";
    const domain = formData.domain?.trim() ?? "";
    if (!name) return toast.error("An internal reference is required.");
    if (!domain) return toast.error("Domain is required.");
    submitAgency({ ...formData, name, domain, notes: formData.notes ?? "" });
  };

  return (
    <CustomModal
      title={editData ? "Edit agency" : "Add agency"}
      open={open}
      onClose={onClose}
      handleConfirmation={handleSubmit}
      isLoading={isPending}
      confirmText={editData ? "Update" : "Create"}
    >
      <div className="space-y-4 py-4">
        {editData && (
          <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-4 pb-4 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
              Status
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span
                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                  status === "approved"
                    ? "bg-emerald-500/10 text-emerald-700"
                    : status === "pending"
                      ? "bg-amber-500/10 text-amber-700"
                      : status === "suspended"
                        ? "bg-slate-200/80 text-slate-600"
                        : "bg-rose-500/10 text-rose-700"
                }`}
              >
                {status}
              </span>
              <div className="flex items-center gap-2">
                {(status === "pending" ||
                  status === "rejected" ||
                  status === "suspended") && (
                  <button
                    type="button"
                    onClick={() => updateStatus("approved")}
                    disabled={isStatusUpdating}
                    className="inline-flex items-center px-3.5 py-1.5 rounded-lg text-sm font-medium text-emerald-700 bg-white border border-emerald-200/80 shadow-sm hover:bg-emerald-50 hover:border-emerald-300 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                  >
                    Approve
                  </button>
                )}
                {(status === "pending" ||
                  status === "approved" ||
                  status === "suspended") && (
                  <button
                    type="button"
                    onClick={() => updateStatus("rejected")}
                    disabled={isStatusUpdating}
                    className="inline-flex items-center px-3.5 py-1.5 rounded-lg text-sm font-medium text-rose-700 bg-white border border-rose-200/80 shadow-sm hover:bg-rose-50 hover:border-rose-300 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                  >
                    Reject
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Internal reference *
          </label>
          <CustomInput
            placeholder="Short label for admins (not shown in the app)"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Domain *
          </label>
          <CustomInput
            placeholder="e.g. agency.com"
            value={formData.domain}
            onChange={(e) =>
              setFormData({ ...formData, domain: e.target.value })
            }
          />
        </div>
        <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50/80 p-4">
          <div>
            <label
              htmlFor="allow-direct-delivery"
              className="block text-sm font-medium text-gray-700"
            >
              Allow delivery without request
            </label>
            <p className="mt-0.5 text-xs text-gray-500">
              The agency can create its own request and deliver it in one step.
              QC is passed and hooks go straight to moderation.
            </p>
          </div>
          <Switch
            id="allow-direct-delivery"
            checked={formData.allow_direct_delivery}
            onCheckedChange={(v) =>
              setFormData({ ...formData, allow_direct_delivery: v })
            }
            className="mt-0.5"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            className="w-full p-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primaryPurple focus:border-primaryPurple outline-none text-sm min-h-[100px]"
            placeholder="Add any additional notes..."
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default AddAgencyModal;
